import React, { useCallback, useEffect, useState } from "react";
import DocumentEditor from "@txtextcontrol/tx-react-ds-document-editor";
import {
  MenuItem,
  TextField,
  FormHelperText,
  Button,
  FormControl,
  Select,
  InputLabel,
  Typography,
  Paper,
  Box,
  IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import styles from "./NewContract.module.css";
import config from "../../../../service/config";
import Lottie from "lottie-react";
import load from "./LoadGIF.json";
import axios from "axios";
import { DateTimePicker, DateTimePickerTabs } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import companyStore from "../../CompanyStore";
import { Buffer } from "buffer";
import { useAllProduct } from "../../../api-context/commonApiContext";
import { Close } from "@mui/icons-material";
import ContractSuccess from "../SuccessView/ContractSuccess";
import { Spin } from "antd";

const NewContract = ({ className = "" }) => {
  const { authTokens, userName, loader, setLoader, isTokenValid, getNewTokens } = useAllProduct();
  const navigate = useNavigate();
  const [file, setFile] = useState([]);
  const [wordDoc, setwordDoc] = useState(null);
  const [adminName, setAdminName] = useState(userName);
  const [contractAdminName, setContractAdminName] = useState("");
  const [parties, setParties] = useState([
    { name: "", email: "", company_name: "", access: "", status: "Not Finalized" },
  ]);
  const formdata = new FormData();
  const [agreementName, setAgreementName] = useState("");
  const [expiryDate, setExpiryDate] = useState(null);
  const [clicked, setClicked] = useState(false);
  const [success, setSuccess] = useState(false);
  const [editIndex, setEditIndex] = useState("");
  const [formErrors, setFormErrors] = useState({
    agreementName: "",
    adminName: "",
    parties: [],
    wordDoc: "",
  });

  useEffect(() => {
    setAdminName(userName);
  }, []);
  const onFrameContainerClick = useCallback(() => {
    navigate("/content/contract/main");
  }, [navigate]);

  const handleRoleChange = (index, newRole) => {
    const newParties = [...parties];

    if (newRole === "edit") {
      newParties.forEach((party, i) => {
        if (i !== index && party.access === "edit") {
          party.access = "view";
        }
      });
      setEditIndex(index); // Set the index of the party that has "edit"
    } else if (editIndex === index) {
      setEditIndex(-1); // Reset edit index if the "edit" party is changed to "view"
    }

    newParties[index].access = newRole; // Update the new access
    setParties(newParties);
  };

  const handleAddParty = () => {
    setParties([...parties, { name: "", email: "", company_name: "", access: "Viewer", status: "Not Finalized" }]);
  };

  const handleRemoveParty = (index) => {
    if (parties.length > 1) {
      setParties(parties.filter((_, i) => i !== index));
    }
  };

  const handleClear = () => {
    setAgreementName("");
    setAdminName("");
    setParties([{ name: "", email: "", company_name: "", access: "", status: "Not Finalized" }]);
    setFormErrors({ parties: [] });
  };

  const handleContinue = async () => {
    const errors = {
      agreementName: "",
      adminName: "",
      parties: [],
    };

    // Validation for agreementName
    const isAgreementNameValid = agreementName.trim() !== "";
    if (!isAgreementNameValid) {
      errors.agreementName = "Contract name is required.";
    } else if (agreementName.trim().length < 3) {
      errors.agreementName = "Contract name must be at least 3 characters.";
    } else if (agreementName.trim().length > 45) {
      errors.agreementName = "Contract should not exceed 45 characters.";
    } else if (!/^[a-zA-Z0-9 ]+$/.test(agreementName.trim())) {
      errors.agreementName = "Contract name cannot contain special characters.";
    }

    // Validation for adminName (assuming it's required)
    const isAdminNameValid = adminName.trim() !== "";
    if (!isAdminNameValid) {
      errors.adminName = "Admin name is required.";
    }

    // Validation for parties
    parties.forEach((party, index) => {
      const partyErrors = [];

      // Name validation
      if (party.name.trim() === "") {
        partyErrors.push("Name is required.");
      } else if (party.name.trim().length < 3) {
        partyErrors.push("Name should be at least 3 characters.");
      } else if (!/^[A-Za-z\s]+$/.test(party.name.trim())) {
        partyErrors.push("Name should contain only letters.");
      }

      // Email validation
      if (party.email.trim() === "") {
        partyErrors.push("Email is required.");
      } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(party.email)) {
        partyErrors.push("Email is invalid.");
      }

      // Company name validation
      if (party.company_name.trim() === "") {
        partyErrors.push("Company name is required.");
      } else if (party.company_name.trim().length < 3) {
        partyErrors.push("Company name should be at least 3 characters.");
      } else if (party.company_name.trim().length > 45) {
        partyErrors.push("Company name should not exceed 45 characters.");
      } else if (/[^a-zA-Z\s]/.test(party.company_name.trim())) {
        partyErrors.push("Company name should contain only letters.");
      }

      // Access level validation
      if (party.access.trim() === "") {
        partyErrors.push("Access level is required.");
      }

      errors.parties[index] = partyErrors;
    });

    setFormErrors(errors);

    // Check if there are any errors
    if (errors.agreementName || errors.adminName || errors.parties.some((partyErrors) => partyErrors.length > 0)) {
      return;
    }

    const hasEditor = parties.some((party) => party.access.trim().toUpperCase() === "EDIT");
    if (!hasEditor) {
      alert("At least one party must have EDITOR access.");
      return;
    }

    if (!wordDoc || (wordDoc.type == "" && wordDoc.type == "")) {
      alert("Please upload a valid word document (max size 10MB)");
      return;
    }

    const filteredParties = parties.filter((party) => party.name.trim() !== "" && party.email.trim() !== "");
    if (filteredParties.length < 2) {
      alert("Please add at least two parties to the agreement.");
      return;
    }

    const emailSet = new Set();
    const hasDuplicateEmail = filteredParties.some((party) => {
      if (emailSet.has(party.email)) return true;
      emailSet.add(party.email);
      return false;
    });

    if (hasDuplicateEmail) {
      alert("Each party must have a unique email address.");
      return;
    }

    if (!authTokens || authTokens.idToken.length === 0) {
      console.log("No auth tokens available, skipping contract creation.");
      return;
    }

    try {
      setClicked(true);
      setLoader(true);

      // Validate token before API call
      if (!isTokenValid(authTokens.idToken)) {
        console.log("Token invalid or expired, refreshing...");
        await getNewTokens();
        const updatedAuthTokens = JSON.parse(localStorage.getItem("authTokens"));
        if (!updatedAuthTokens?.idToken || !isTokenValid(updatedAuthTokens.idToken)) {
          console.error("Failed to refresh token or new token is invalid.");
          setLoader(false);
          return;
        }
        authTokens.idToken = updatedAuthTokens.idToken;
      }
      var newTxt = wordDoc;
      const buffer = Buffer.from(newTxt, "utf8");
      const blob = new Blob(
        [buffer] /* , { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" } */,
      );

      var reader = new FileReader();
      reader.onloadend = function () {
        var base64data = reader.result;
      };
      var currentuser = "";
      filteredParties.forEach((data) => {
        if (data.access == "edit") {
          currentuser = data.email;
        }
      });
      reader.readAsDataURL(blob);

      // Prepare formdata

      formdata.set("escrow_name", companyStore.escrowName);
      formdata.set("contract_name", agreementName);
      formdata.set("admin", JSON.stringify({ name: adminName, email: companyStore.AdminEmail }));
      formdata.set("escrow_id", companyStore.escrowId);
      formdata.set("counter_parties", JSON.stringify(filteredParties));
      formdata.set("status", "sent");
      formdata.set("created_date", new Date().toISOString());
      formdata.set("current_edit_access", filteredParties.find((p) => p.access.toLowerCase() === "edit")?.email || "");
      formdata.set("file", blob);

      // Create contract
      const res = await axios.post(`${config.mock_api_endpoint}/api/createContract`, formdata, {
        headers: {
          aws_cognito_id_token: authTokens.idToken,
          "Content-Type": "multipart/form-data",
        },
      });

      console.log("inside create contract");
      companyStore.setContractID(res.data.contract.contract_id);

      // Send emails to counter parties
      for (const data1 of res.data.contract.counter_parties) {
        console.log("parties--->", data1);
        console.log("data : %o", data1);

        let bodyHtml, emailSubject, actionText;
        if (data1.access.toLowerCase() === "view") {
          bodyHtml = `
            <html>
            <head>
                <style>
                    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
                    .container { max-width: 600px; margin: auto; padding: 20px; }
                    h1 { color: #007BFF; }
                    a { color: #007BFF; text-decoration: none; }
                    .button { display: inline-block; padding: 10px 20px; font-size: 16px; color: white; background-color: #007BFF; border: none; border-radius: 5px; text-decoration: none; text-align: center; }
                    .footer { margin-top: 20px; font-size: 0.9em; color: #777; }
                </style>
            </head>
            <body style="margin: 0; padding: 0">
              <div style="font-size: 0px; line-height: 1px; mso-line-height-rule: exactly; display: none; max-width: 0px; max-height: 0px; opacity: 0; overflow: hidden; mso-hide: all"></div>
              <center lang="und" dir="auto" style="width: 100%; table-layout: fixed; -webkit-text-size-adjust: 100%; -ms-text-size-adjust: 100%">
                <table class="bg-fffffe" cellpadding="0" cellspacing="0" border="0" role="presentation" bgcolor="white" width="657.00" style="border-radius: 12px; background-color: white; padding-top: 32px; padding-bottom: 32px; width: 657px; border-spacing: 0; font-family: Sarabun, Tahoma, sans-serif; min-width: 657px; border-collapse: separate">
                  <tr>
                    <td valign="middle" width="100.00%" style="width: 100%; vertical-align: middle">
                      <table cellpadding="0" cellspacing="0" border="0" role="presentation" width="100.00%" style="width: 100%; border-spacing: 0">
                        <tr><td align="center" style="padding-bottom: 13.76px"><img src="cid:logo" width="101" height="50" alt="Logo" /></td></tr>
                        <tr><td align="left" style="padding-top: 13.76px; padding-bottom: 13.72px; padding-left: 32px">
                          <p class="color-4c4e64" width="100.00%" style="font-size: 20px; font-weight: 400; letter-spacing: 0.4px; color: #4C4E64; margin: 0; padding: 0; width: 100%; line-height: 21.44px; mso-line-height-rule: exactly"><span>Dear </span><span style="font-size: 20px; font-weight: 600; letter-spacing: 0.4px; text-align: left">${data1.name},</span></p>
                        </td></tr>
                        <tr><td align="left" style="padding-top: 13.72px; padding-bottom: 15.16px; padding-left: 32px; padding-right: 32px">
                          <p width="100.00%" style="text-align: left; color: #777777; margin: 0; padding: 0; width: 100%"><span class="color-777777" style="font-size: 20px; font-weight: 400; color: #777777; letter-spacing: 0.4px; text-align: left">You have been added as a </span><span style="font-size: 20px; font-weight: 600; color: #333333; letter-spacing: 0.4px; text-align: left">viewer</span><span class="color-777777" style="font-size: 20px; font-weight: 400; color: #777777; letter-spacing: 0.4px; text-align: left"> for the contract titled</span><span class="color-777777" style="font-size: 20px; font-weight: 600; color: #777777; letter-spacing: 0.4px; text-align: left"> </span><span style="font-size: 20px; font-weight: 600; color: #333333; letter-spacing: 0.4px; text-align: left">"${agreementName}"</span><span class="color-777777" style="font-size: 20px; font-weight: 500; color: #777777; letter-spacing: 0.4px; text-align: left">. As a viewer, you can access and review the document, track any changes made, and request editing access if needed.</span></p>
                        </td></tr>
                        <tr><td align="left" style="padding-top: 15.16px; padding-bottom: 7.58px; padding-left: 32px">
                          <p width="100.00%" style="font-size: 20px; font-weight: 700; letter-spacing: 0.4px; color: #333333; margin: 0; padding: 0; width: 100%; line-height: 15.68px; mso-line-height-rule: exactly">Please follow the link below to view the contract:</p>
                        </td></tr>
                        <tr><td style="padding-top: 7.58px; padding-left: 33px; padding-right: 33px">
                          <table class="bg-2348ab" cellpadding="0" cellspacing="0" border="0" role="presentation" bgcolor="#2348AB" width="591.00" height="40.00" style="border-radius: 30px; background-color: #2348AB; width: 591px; height: 40px; border-spacing: 0; border-collapse: separate">
                            <tr><td valign="middle" width="100.00%" height="40.00" style="width: 100%; vertical-align: middle; height: 40px">
                              <a href="${config.doc_link_api_endpoint}${data1.doc_link}" style="text-decoration: none;">
                                <p class="color-fffffe" width="100.00%" style="font-size: 16px; font-weight: 700; letter-spacing: 0.15px; color: white; margin: 0; padding: 0; width: 100%; line-height: 24px; text-align: center; mso-line-height-alt: 24px">View Contract</p>
                              </a>
                            </td></tr>
                          </table>
                        </td></tr>
                        <tr><td align="left" style="padding-top: 13.75px; padding-bottom: 12.15px; padding-left: 32px">
                          <span style="font-size: 20px; font-weight: 500; letter-spacing: 0.4px; text-align: left; color: #777777">Best regards,</span><br />
                          <span style="font-size: 20px; font-weight: 500; letter-spacing: 0.4px; text-align: left; color: #777777;">Digital Onboarding Team</span>
                        </td></tr>
                      </table>
                    </td>
                  </tr>
                </table>
              </center>
            </body>
            </html>
          `;
          emailSubject = `View and Track Changes in Contract - ${agreementName}`;
          actionText = "View Contract";
        } else {
          bodyHtml = `
            <html>
            <head>
                <style>
                    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
                    .container { max-width: 600px; margin: auto; padding: 20px; }
                    h1 { color: #007BFF; }
                    a { color: #007BFF; text-decoration: none; }
                    .button { display: inline-block; padding: 10px 20px; font-size: 16px; color: white; background-color: #007BFF; border: none; border-radius: 5px; text-decoration: none; text-align: center; }
                    .footer { margin-top: 20px; font-size: 0.9em; color: #777; }
                </style>
            </head>
            <body style="margin: 0; padding: 0">
              <div style="font-size: 0px; line-height: 1px; mso-line-height-rule: exactly; display: none; max-width: 0px; max-height: 0px; opacity: 0; overflow: hidden; mso-hide: all"></div>
              <center lang="und" dir="auto" style="width: 100%; table-layout: fixed; -webkit-text-size-adjust: 100%; -ms-text-size-adjust: 100%">
                <table class="bg-fffffe" cellpadding="0" cellspacing="0" border="0" role="presentation" bgcolor="white" width="657.00" style="border-radius: 12px; background-color: white; padding-top: 32px; padding-bottom: 32px; width: 657px; border-spacing: 0; font-family: Sarabun, Tahoma, sans-serif; min-width: 657px; border-collapse: separate">
                  <tr>
                    <td valign="middle" width="100.00%" style="width: 100%; vertical-align: middle">
                      <table cellpadding="0" cellspacing="0" border="0" role="presentation" width="100.00%" style="width: 100%; border-spacing: 0">
                        <tr><td align="center" style="padding-bottom: 13.76px"><img src="cid:logo" width="101" height="50" alt="Logo" /></td></tr>
                        <tr><td align="left" style="padding-top: 13.76px; padding-bottom: 13.72px; padding-left: 32px">
                          <p class="color-4c4e64" width="100.00%" style="font-size: 20px; font-weight: 400; letter-spacing: 0.4px; color: #4C4E64; margin: 0; padding: 0; width: 100%; line-height: 21.44px; mso-line-height-rule: exactly"><span>Dear </span><span style="font-size: 20px; font-weight: 600; letter-spacing: 0.4px; text-align: left">${data1.name},</span></p>
                        </td></tr>
                        <tr><td align="left" style="padding-top: 13.72px; padding-bottom: 15.16px; padding-left: 32px; padding-right: 32px">
                          <p width="100.00%" style="text-align: left; color: #777777; margin: 0; padding: 0; width: 100%"><span class="color-777777" style="font-size: 20px; font-weight: 400; color: #777777; letter-spacing: 0.4px; text-align: left">You have been assigned as an </span><span style="font-size: 20px; font-weight: 600; color: #333333; letter-spacing: 0.4px; text-align: left">Editor</span><span class="color-777777" style="font-size: 20px; font-weight: 400; color: #777777; letter-spacing: 0.4px; text-align: left"> for the contract titled</span><span class="color-777777" style="font-size: 20px; font-weight: 600; color: #777777; letter-spacing: 0.4px; text-align: left"> </span><span style="font-size: 20px; font-weight: 600; color: #333333; letter-spacing: 0.4px; text-align: left">"${agreementName}"</span><span class="color-777777" style="font-size: 20px; font-weight: 500; color: #777777; letter-spacing: 0.4px; text-align: left">. As an editor, you can review and make changes to the document directly through our document editor.</span></p>
                        </td></tr>
                        <tr><td align="left" style="padding-top: 15.16px; padding-bottom: 7.58px; padding-left: 32px">
                          <p width="100.00%" style="font-size: 20px; font-weight: 700; letter-spacing: 0.4px; color: #333333; margin: 0; padding: 0; width: 100%; line-height: 15.68px; mso-line-height-rule: exactly">Please follow the link below to edit the contract:</p>
                        </td></tr>
                        <tr><td style="padding-top: 7.58px; padding-left: 33px; padding-right: 33px">
                          <table class="bg-2348ab" cellpadding="0" cellspacing="0" border="0" role="presentation" bgcolor="#2348AB" width="591.00" height="40.00" style="border-radius: 30px; background-color: #2348AB; width: 591px; height: 40px; border-spacing: 0; border-collapse: separate">
                            <tr><td valign="middle" width="100.00%" height="40.00" style="width: 100%; vertical-align: middle; height: 40px">
                              <a href="${config.doc_link_api_endpoint}${data1.doc_link}" style="text-decoration: none;">
                                <p class="color-fffffe" width="100.00%" style="font-size: 16px; font-weight: 700; letter-spacing: 0.15px; color: white; margin: 0; padding: 0; width: 100%; line-height: 24px; text-align: center; mso-line-height-alt: 24px">Edit Contract</p>
                              </a>
                            </td></tr>
                          </table>
                        </td></tr>
                        <tr><td align="left" style="padding-top: 13.75px; padding-bottom: 12.15px; padding-left: 32px">
                          <span style="font-size: 20px; font-weight: 500; letter-spacing: 0.4px; text-align: left; color: #777777">Best regards,</span><br />
                          <span style="font-size: 20px; font-weight: 500; letter-spacing: 0.4px; text-align: left; color: #777777;">Digital Onboarding Team</span>
                        </td></tr>
                      </table>
                    </td>
                  </tr>
                </table>
              </center>
            </body>
            </html>
          `;
          emailSubject = `Access and Edit Contract - ${agreementName}`;
          actionText = "Edit Contract";
        }

        const emailPayload = {
          to: data1.email,
          body: bodyHtml,
          subject: emailSubject,
        };

        await axios.post(`${config.email_api_endpoint}/send-email`, emailPayload, {
          headers: { "Content-Type": "multipart/form-data", "x-api-key": config.email_key },
        });
        console.log(`Email sent to ${data1.email} as ${data1.access}`);
      }

      setSuccess(true);
      // navigate(`/content/contract/contractdetail`); // Uncomment if navigation is desired
    } catch (err) {
      console.error("Error creating contract or sending emails:", err);
      if (err.response?.data?.error === "Token has expired. Please log in again.") {
        console.log("Token expired during request, refreshing...");
        await getNewTokens();
        const updatedAuthTokens = JSON.parse(localStorage.getItem("authTokens"));
        if (updatedAuthTokens?.idToken && isTokenValid(updatedAuthTokens.idToken)) {
          return handleContinue();
        }
      }
    } finally {
      setLoader(false);
      setClicked(false); // Reset clicked state
    }
  };

  // function uploadFile(event) {
  //   let file = event.target.files[0];

  //   if (file) {
  //     var fileReader = new FileReader();
  //     fileReader.onload = (e) => {
  //       console.log("fileData-->",e);

  //       var contract = e.target.result;

  //       setwordDoc(contract);
  //     };
  //     fileReader.readAsDataURL(file);
  //   }
  // }

  function uploadFile(event) {
    let selectedFile = event.target.files[0];

    if (selectedFile) {
      // Check file size (10MB = 10 * 1024 * 1024 bytes)
      const maxSize = 10 * 1024 * 1024;
      if (selectedFile.size > maxSize) {
        alert("File size exceeds 10MB. Please upload a smaller file.");
        return; // Exit the function early
      }

      // Check file type (only allow Word documents)
      const allowedTypes = [
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
      ];

      if (!allowedTypes.includes(selectedFile.type)) {
        alert("Invalid file type. Please upload a valid word document (max size 10MB)");
        return; // Exit the function early
      }

      // If the file is valid, read and process it
      const fileReader = new FileReader();
      fileReader.onload = (e) => {
        var contract = e.target.result;
        // console.log("contract", contract);

        setwordDoc(contract);
        setFile({
          name: selectedFile.name,
          size: (selectedFile.size / (1024 * 1024)).toFixed(2) + " MB", // Convert bytes to MB
          preview: "/word-icon.svg", // Fixed preview icon for Word files
        });
      };

      fileReader.readAsDataURL(selectedFile);
    }
  }

  function handleRemoveFile() {
    setFile({});
  }
  useEffect(() => {
    const styleSheet = document.styleSheets[0];
    styleSheet.insertRule(".ant-spin-nested-loading { width: 100% !important; }", styleSheet.cssRules.length);
  }, []);
  console.log("formErrors", file);

  return (
    <div className={`${styles.frameParent}`}>
      {/* <div className={styles.framehidden}></div> */}
      {!success ? (
        <>
          <Spin
            size="large"
            tip="Loading..."
            spinning={loader}
            className="d-flex justify-content-center align-items-center"
            wrapperClassName="spin-nested-loading"
            style={{ maxHeight: "none" }}
          >
            <div className={styles.frameWrapper} onClick={onFrameContainerClick}>
              <div className={styles.arrowNarrowLeftParent}>
                <img className={styles.arrowNarrowLeftIcon} alt="" src="/arrownarrowleft.svg" />
                <div className={styles.back}>Back</div>
              </div>
            </div>
            <div className="mb-2 mt-2">
              <b
                className={styles.escrowName}
                title={companyStore.escrowName.length > 50 ? companyStore.escrowName : ""}
              >
                {companyStore.escrowName.length > 50
                  ? companyStore.escrowName.slice(0, 50) + "..."
                  : companyStore.escrowName}
              </b>
            </div>
            <div className={`${styles.frameGroup}`}>
              <div className={`${styles.frameContainer} d-flex justify-content-center`}>
                <div className={styles.uploadTheFinalDraftOfAgreParent}>
                  <div className={styles.uploadTheFinal}>+ Upload the agreement</div>
                  <div className={styles.uploadTheFinal}>Upload only word Document</div>
                </div>

                {Object.keys(file).length > 0 && (
                  <Paper
                    variant="outlined"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      padding: 1,
                      marginBottom: 1,
                      borderRadius: 2,
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        marginRight: 2,
                      }}
                    >
                      <img
                        src="/word-icon.svg"
                        alt={file.name}
                        style={{
                          width: 40,
                          height: 40,
                          objectFit: "cover",
                          borderRadius: 4,
                        }}
                      />
                    </Box>

                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        marginRight: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {file.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" className="w-100">
                        {file.size}
                      </Typography>
                    </Box>
                    <IconButton onClick={handleRemoveFile} size="small">
                      <Close fontSize="small" />
                    </IconButton>
                  </Paper>
                )}

                <Button
                  variant="contained"
                  component="label"
                  startIcon={
                    <img
                      src="/fileUpload.svg"
                      alt="Upload Icon"
                      style={{
                        width: 24,
                        height: 24,
                      }}
                    />
                  }
                  sx={{
                    // marginTop: 2,
                    padding: 1.5,
                    border: "2px dotted #AAAAAA",
                    borderRadius: 2,
                    fontSize: "14px",
                    width: "100%",
                    backgroundColor: "#ffffff",
                    color: "#777777",
                    boxShadow: "none",
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                >
                  {/* Drag & drop the file here or &nbsp;{" "} */}
                  <span
                    style={{
                      textDecoration: "underline",
                      color: "#2348AB",
                      fontWeight: 700,
                    }}
                  >
                    Click to upload
                  </span>
                  <input type="file" hidden accept=".doc,.docx" onChange={uploadFile} />
                </Button>

                <Typography variant="caption" display="block" sx={{ textAlign: "center", color: "#757575" }}>
                  (File type DOCX / Max Total File Size 10MB)
                </Typography>
              </div>
              <div className={`${styles.frameDiv} d-flex justify-content-center`}>
                <div className={styles.partiesParent}>
                  <div className={styles.parties}>Enter Contract details</div>

                  <Button className={styles.inputParent} onClick={handleClear}>
                    <div className={styles.input1}>clear ALL</div>
                  </Button>
                </div>
                <ol>
                  <div className={styles.frameParent1}>
                    <div className={styles.mobileMenuParent}>
                      <TextField
                        className={styles.mobileMenu}
                        color="primary"
                        label="Name of Contract"
                        variant="outlined"
                        value={agreementName}
                        onChange={(e) => setAgreementName(e.target.value)}
                        sx={{
                          "& .MuiInputBase-root": { height: "56px", borderRadius: "12px" },
                          width: "309px",
                        }}
                        error={!!formErrors.agreementName}
                        helperText={formErrors.agreementName}
                      />
                      <TextField
                        className={styles.mobileMenu}
                        color="primary"
                        label="Contract Admin's name"
                        variant="outlined"
                        value={userName}
                        disabled={true}
                        sx={{
                          "& .MuiInputBase-root": { height: "56px", borderRadius: "12px" },
                          width: "309px",
                        }}
                        error={!!formErrors.adminName}
                        helperText={formErrors.adminName}
                      />
                    </div>
                  </div>
                  <div className={`${styles.partiesOverflow} w-100 mt-4`}>
                    {parties.map((party, index) => (
                      <div key={index} className={styles.frameWrapper1}>
                        <div className={styles.frame1}>
                          <li className={styles.partieslistitem}>{index + 1}. Counter Party</li>
                          {index >= 1 && (
                            <Button className={styles.inputParent} onClick={() => handleRemoveParty(index)}>
                              <div className={styles.input1}>Remove</div>
                              <img className={styles.arrowNarrowLeftIcon} alt="" src="/minuscircle.svg" />
                            </Button>
                          )}
                          {/* {index == 0 && (
                            <Button className={styles.inputParent} onClick={handleClearAll}>
                              <div className={styles.input1}>clear</div>
                            </Button>
                          )} */}
                        </div>
                        <div className={styles.mobileMenuParent1}>
                          <div className={styles.mobileMenuParent}>
                            <div className={styles.mobileMenu2}>
                              <TextField
                                className={styles.field}
                                color="primary"
                                label="Name"
                                variant="outlined"
                                value={party.name}
                                onChange={(e) => {
                                  const newParties = [...parties];
                                  newParties[index].name = e.target.value;
                                  setParties(newParties);
                                }}
                                sx={{
                                  "& .MuiInputBase-root": { height: "56px", borderRadius: "12px" },
                                  width: "309px",
                                }}
                                error={
                                  !!formErrors.parties[index]?.find(
                                    (e) =>
                                      e === "Name is required." ||
                                      e === "Name should be at least 3 characters." ||
                                      e === "Name should contain only letters.",
                                  )
                                }
                                helperText={
                                  formErrors.parties[index]?.find((e) => e === "Name is required.") ||
                                  formErrors.parties[index]?.find((e) => e === "Name should contain only letters.") ||
                                  formErrors.parties[index]?.find(
                                    (e) => e === "Name should be at least 3 characters.",
                                  ) ||
                                  ""
                                }
                              />
                              <img className={styles.trailingIcon2} alt="" src="/trailingicon-2.svg" />
                            </div>
                            <TextField
                              className={styles.mobileMenu3}
                              color="primary"
                              label="Company Name"
                              variant="outlined"
                              value={party.company_name}
                              onChange={(e) => {
                                const newParties = [...parties];
                                newParties[index].company_name = e.target.value;
                                setParties(newParties);
                              }}
                              sx={{
                                "& .MuiInputBase-root": { height: "56px", borderRadius: "12px" },
                                width: "309px",
                              }}
                              error={
                                !!formErrors.parties[index]?.find(
                                  (e) =>
                                    e === "Company name is required." ||
                                    e === "Company name should not exceed 45 characters." ||
                                    e === "Company name should be at least 3 characters." ||
                                    e === "Company name should contain only letters.",
                                )
                              }
                              helperText={formErrors.parties[index]?.find(
                                (e) =>
                                  e === "Company name is required." ||
                                  e === "Company name should not exceed 45 characters." ||
                                  e === "Company name should be at least 3 characters." ||
                                  e === "Company name should contain only letters.",
                                "",
                              )}
                            />
                          </div>
                          <div className={styles.mobileMenuParent}>
                            <TextField
                              className={styles.mobileMenu3}
                              color="primary"
                              label="Email ID"
                              variant="outlined"
                              type="email"
                              value={party.email}
                              onChange={(e) => {
                                const newParties = [...parties];
                                newParties[index].email = e.target.value;
                                setParties(newParties);
                              }}
                              sx={{
                                "& .MuiInputBase-root": { height: "56px", borderRadius: "12px" },
                                width: "309px",
                              }}
                              error={
                                !!formErrors.parties[index]?.find(
                                  (e) => e === "Email is required." || e === "Email is invalid.",
                                )
                              }
                              helperText={
                                formErrors.parties[index]?.find((e) => e === "Email is required.") ||
                                formErrors.parties[index]?.find((e) => e === "Email is invalid.") ||
                                ""
                              }
                            />
                            <FormControl
                              className={styles.editorParent}
                              variant="outlined"
                              error={!!formErrors.parties[index]?.find((e) => e === "Access level is required.")}
                            >
                              <InputLabel color="primary">Access</InputLabel>
                              <Select
                                labelId={`access-select-label-${index}`}
                                id={`access-select-${index}`}
                                value={party.access || ""} // Ensure a valid value is always present
                                label="Access"
                                onChange={(e) => handleRoleChange(index, e.target.value)} // Make sure this handler is updating state properly
                                sx={{ borderRadius: "12px", width: "309px" }}
                              >
                                <MenuItem value="view">VIEWER</MenuItem>
                                <MenuItem value="edit">EDITOR</MenuItem>
                              </Select>
                              <FormHelperText>
                                {formErrors.parties[index]?.find((e) => e === "Access level is required.") || ""}
                              </FormHelperText>
                            </FormControl>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ol>

                <div className={styles.containbuttons}>
                  <Button className={styles.addDirector} onClick={handleAddParty}>
                    <img className={styles.boxArrowInDownIcon} alt="" src="/boxarrowindown1.svg" />
                    <img className={styles.arrowNarrowLeftIcon} alt="" src="/pluscircle1.svg" />
                    <b className={styles.addDirector1}>Add Parties</b>
                  </Button>
                  <div
                    className={styles.generateRequest}
                    onClick={() => {
                      handleContinue();
                    }}
                  >
                    {clicked && (
                      <Lottie className={styles.httpslottiefilescomanimatIcon} animationData={load} loop={true} />
                    )}
                    <b className={styles.generateRequest1}>Continue</b>
                  </div>
                </div>
              </div>
            </div>
          </Spin>
        </>
      ) : (
        <>
          <ContractSuccess msg={"contract_created"} />
        </>
      )}
    </div>
  );
};

NewContract.propTypes = {
  className: PropTypes.string,
};

export default NewContract;
